import "./SectionView.less";

import { notification, Alert, Badge, Breadcrumb, Button, Dropdown, Icon, Menu, Spin, Tooltip } from "antd";
import { ClickParam } from "antd/lib/menu";
import { Location } from "history";
import { cloneDeep, isEqual } from "lodash";
import { curry, __ } from "lodash/fp";
import * as QueryString from "query-string";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { SortableContainer, SortableElement, SortEnd, SortEvent, SortEventWithTag } from "react-sortable-hoc";

import { MetadataApi, OrderedEntryGroupApi, OrderedListingGroupApi, OrderedSectionApi, SectionApi, SectionVersionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";
import {
	OrderedEntryGroup,
	OrderedListingGroup,
	OrderedSection,
	Permission,
	Section,
	SectionVersion,
	User
} from "lib/data/model";
import { Right, VersionState } from "lib/data/model/enumerations";
import { SingleRecordPermission } from "lib/data/model/security/SingleRecordPermission";
import { ValidationError } from "lib/data/transfer/ValidationError";
import { Report } from "lib/enumerations";
import { LoginService, PermissionService, StateService, UtilityService } from "lib/services";
import { Event, EventDispatcher } from "lib/EventDispatcher";

import { AuthorizedComponent, GradientBar, NewSectionArea } from "components/general";
import { ReportPage, SectionPage } from "components/pages";
import { EntryGroupView, SectionDrawerView, SectionMetadataView } from "components/views";
import { ListingGroupView } from "../ListingGroupView";
import { OrderedSectionView } from "../OrderedSectionView";


interface ISectionViewProps extends IBaseProps, RouteComponentProps<any> {
	id?: number;
}

interface ISectionViewState {
	isLoaded: boolean;
	isLoading: boolean;
	isReloading: boolean;
	showHighlighting: boolean;
	drawerVisible: boolean;
	isDraft: boolean;
	deletingEntryGroups: number[];
	deletingListingGroups: number[];
	deletingSections: number[];
	validationErrors?: ValidationError | true;
	isDragDropCanceled: boolean;
}


export class SectionView extends React.Component<ISectionViewProps, ISectionViewState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private searchedWords: string[] = [];
	private highlightWords: string[] = [];
	private permission!: Permission;
	private loggedInUser?: User;

	private section!: Section;
	private hierarchy: Section[] = [];
	private currentVersion!: SectionVersion;
	private children: Array<OrderedEntryGroup | OrderedListingGroup | OrderedSection> = [];

	constructor(props: ISectionViewProps) {

		super(props);

		this.state = {
			isLoaded: false,
			isLoading: true,
			isReloading: false,
			showHighlighting: false,
			drawerVisible: false,
			isDraft: false,
			deletingEntryGroups: [],
			deletingListingGroups: [],
			deletingSections: [],
			isDragDropCanceled: true
		};
	}

	public async componentWillMount() {

		// Process Any Search
		this.processSearch(this.props.location);

		// Get Logged In User
		this.loggedInUser = await LoginService.getUserIfExists();

		// Register For Logged In Event
		EventDispatcher.register(Event.LoggedIn, this.onLoggedIn);

		// Register For Logged Out Event
		EventDispatcher.register(Event.LoggedOut, this.onLoggedOut);

		// Reload Section
		await this.loadSection(this.props.match.params.id);
	}

	public async componentWillReceiveProps(newProps: ISectionViewProps) {

		// Process Any Search
		this.processSearch(newProps.location);

		// Get Logged In User
		this.loggedInUser = await LoginService.getUserIfExists();

		// Check For Different Section
		if (this.section === undefined || newProps.match.params.id !== this.section.id) {

			// Reload Section
			await this.loadSection(newProps.match.params.id);
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();

		// Unregister For Logged In Event
		EventDispatcher.unregister(Event.LoggedIn, this.onLoggedIn);

		// Unregister For Logged Out Event
		EventDispatcher.unregister(Event.LoggedOut, this.onLoggedOut);
	}

	public render() {

		// Define Class Name
		const className = "section-view" + (this.props.className ? ` ${this.props.className}` : "");

		// Check For Loaded Section
		if (this.state.isLoading && !this.state.isLoaded) {

			// Render Loading
			return (
				<section>
					<h4>Loading...</h4>
				</section>
			);
		}
		else {

			// Check Section
			if (!this.state.isLoaded) {

				// Render Error
				return (
					<section>
						<h3>Record Not Found</h3>
					</section>
				);
			}
			else {

				// Build Breadcrumbs
				const breadcrumbs = [];

				// Loop Through Hierarchy
				this.hierarchy.forEach((section, index) => {

					let currentChildSectionVersion;
					const currentChildSectionId = section.id ? section.id : undefined;
					let permission: Permission = new Permission(Right.Update, Section.$type);

					// Check Section ID
					if (currentChildSectionId !== undefined) {

						// Instantiate Record Permission
						permission = new SingleRecordPermission(Right.Update, Section.$type, currentChildSectionId);
					}

					if (this.loggedInUser
						&& PermissionService.userHasPermission(this.loggedInUser, permission)
						&& section.draft
					) {
						currentChildSectionVersion = section.draft;
					}
					else if (section.published) {
						currentChildSectionVersion = section.published;
					}

					if (currentChildSectionVersion) {

						breadcrumbs.push(
							<Breadcrumb.Item key={index}>
								<Link to={`${SectionPage.BasePath}/${section.id}`}>
									{currentChildSectionVersion.name}
								</Link>
							</Breadcrumb.Item>
						);
					}
				});

				// Add Current Breadcrumb
				// breadcrumbs.push(<h6 key={this.hierarchy.length}>Current</h6>);
				breadcrumbs.push(<Breadcrumb.Item key={this.hierarchy.length}>Current</Breadcrumb.Item>);

				// Render Normally
				return (
					<section className={className}>

						{/* Breadcrumbs */}
						{(
							breadcrumbs.length > 0
								?
								<Breadcrumb>
									{breadcrumbs}
								</Breadcrumb>
								:
								undefined
						)}

						{/* Toggle Search Keyword Highlighting and Return to Results */}

						{(
							this.searchedWords.length > 0
								?
								<div className="toggle-highlighting">

									<Button.Group>
										<Button className="button-group" icon="left" onClick={this.onReturnToSearchButtonClick}>Return to Results</Button>
										<Button onClick={this.onSearchHighlightToggle}>{(this.state.showHighlighting ? "Remove Highlighting" : "Show Highlighting")}<Icon type="highlight" /></Button>
									</Button.Group>

								</div>
								:
								undefined
						)}

						{/* Section Title */}
						<section className="section-view-header">

							<GradientBar
								title={(this.section.published === undefined ? this.currentVersion.name : this.currentVersion.name)}
								titleAppendage={(
									this.section.published === undefined
										?
										(
											<Badge offset={[25, -7.5]} count={<div className="unpublished-section-badge">unpublished</div>} />
										)
										:
										undefined
								)}
								size="large"
								className="section-view-title-bar"
								highlightWords={this.highlightWords}
								actions={[
									(
										<AuthorizedComponent key="toggle-draft" permission={this.permission}>
											{(
												this.section.published !== undefined
													?
													(
														this.state.isDraft
															?
															<Tooltip title="Close Draft Mode">
																<Button
																	icon="close-circle"
																	onClick={this.onDraftClose}
																/>
															</Tooltip>
															:
															<Tooltip title="Open Draft Mode">
																<Button
																	icon="form"
																	onClick={this.reloadDraft}
																/>
															</Tooltip>
													)
													:
													null
											)}
										</AuthorizedComponent>
									),
									(
										<AuthorizedComponent key="toggle-draft" permission={this.permission}>
											{(
												this.state.isDraft
													?
													<Tooltip title="Validate Draft">
														<Button
															icon="issues-close"
															onClick={this.onDraftValidate}
														/>
													</Tooltip>
													:
													null
											)}
										</AuthorizedComponent>
									),
									(
										this.section.published !== undefined && this.state.isDraft === false
											?
											(
												<Tooltip key="export-pdf" title="Export PDF">
													<Dropdown
														trigger={["click"]}
														overlay={(
															<Menu onClick={this.onExportClick}>
																<Menu.Item key={Report.SectionReport}>Print Current Page Only</Menu.Item>
																<Menu.Item key={Report.SectionTreeReport}>Print Current Page &amp; Subpages</Menu.Item>
															</Menu>
														)}
													>
														<Button
															key="export-pdf"
															icon="file-pdf"
														/>
													</Dropdown>
												</Tooltip>
											)
											:
											null
									),
									(
										this.state.isDraft === true
											?
											(
												<AuthorizedComponent key="show-drawer" permission={this.permission}>
													<Tooltip title="Show Section Details">
														<Button
															icon="control"
															onClick={this.showDrawer}
														/>
													</Tooltip>
												</AuthorizedComponent>
											)
											:
											null
									)
								].filter((item) => item !== null) as Array<React.ReactElement<AuthorizedComponent<Button>>>}
							/>
						</section>

						{/* Check If Reloading */}
						{(this.state.isReloading
							?
							<Spin />
							:
							<React.Fragment>
								<Spin
									tip="Loading..."
									spinning={this.state.isLoading}
								>
									{
										this.state.isDraft
											?
											<Alert
												type="info"
												showIcon={true}
												message="You are currently viewing a DRAFT copy."
												description="Any changes made will not be visible to the public until the draft is PUBLISHED."
											/>
											:
											null
									}

									{
										this.state.isDraft && this.state.validationErrors !== undefined && this.state.validationErrors instanceof ValidationError
											?
											<Alert
												type="warning"
												showIcon={true}
												message="The following issues are out of compliance with the template:"
												description={(
													<ul>
														{
															this.state.validationErrors.getKeyedErrorList().map((error, index) => <li key={index}>{error}</li>)
														}
													</ul>
												)}
											/>
											:
											null
									}

									{
										this.state.isDraft && this.state.validationErrors !== undefined && this.state.validationErrors === true
											?
											<Alert
												type="success"
												showIcon={true}
												message="Validated"
												description="There are no compliance issues with the template"
											/>
											:
											null
									}

									{/* Section Content */}
									< section className="section-view-content">

										{/* Section Content Description and/or Information */}
										<header className="content-header">

											{
												this.currentVersion.metadata !== undefined && (!this.state.isDraft || this.section.template.dataTemplate !== undefined)
													?
													<SectionMetadataView
														section={this.section}
														metadata={this.currentVersion.metadata}
														highlightWords={this.highlightWords}
														requiredPermission={this.permission}
														isEditing={this.state.isDraft}
													/>
													:
													undefined
											}

										</header>

										{/* Section Components */}

										<SortableComponentContainer
											section={this.section}
											version={this.currentVersion}
											components={this.children}
											permission={this.permission}
											highlightingWords={this.highlightWords}
											state={this.state}
											onEntryGroupDelete={this.onEntryGroupDelete}
											onListingGroupDelete={this.onListingGroupDelete}
											onSectionDelete={this.onSectionDelete}
											onSectionClick={this.onSectionClick}
											shouldCancelStart={this.isSortingDisabled}
											onSortEnd={this.onComponentDrag}
											lockAxis="y"
											distance={5}
										/>

										<section>
											<AuthorizedComponent permission={this.permission}>
												<SectionDrawerView
													section={this.section}
													version={this.currentVersion}
													onClose={this.onDrawerClose}
													visible={this.state.drawerVisible}
													isEditable={this.state.isDraft}
													onNameChange={this.onDraftNameChange}
													onDraftPublish={this.onDraftPublish}
													onDraftRevert={this.onDraftRevert}
												/>
											</AuthorizedComponent>
										</section>
									</section>

									{
										this.state.isDraft
											?
											<AuthorizedComponent permission={this.permission}>
												<NewSectionArea
													section={this.section}
													sectionTemplate={this.section.template}
													sectionVersion={this.currentVersion}
													onSave={this.reloadDraft}
												/>
											</AuthorizedComponent>
											:
											""
									}
								</Spin>
							</React.Fragment>
						)}

					</section >
				);
			}
		}
	}

	private onComponentDrag = async (sortEnd: SortEnd) => {

		if (sortEnd.newIndex !== sortEnd.oldIndex) {

			const clonedChildren = cloneDeep(this.children);
			const draggedComponent = clonedChildren.splice(sortEnd.oldIndex, 1);
			clonedChildren.splice(sortEnd.newIndex, 0, ...draggedComponent);

			const saveRequests: Array<Promise<any>> = [];

			clonedChildren.forEach((child, index) => {

				child.order = index;

				switch (child.$type) {

					case OrderedEntryGroup.$type:
						saveRequests.push(OrderedEntryGroupApi.save(this.section, child as OrderedEntryGroup));
						break;

					case OrderedListingGroup.$type:
						saveRequests.push(OrderedListingGroupApi.save(this.section, child as OrderedListingGroup));
						break;

					case OrderedSection.$type:
						const clonedChild = cloneDeep(child);
						(clonedChild.item as any) = null;
						saveRequests.push(OrderedSectionApi.update(this.section, clonedChild as OrderedSection));
						break;
				}
			});

			this.children = clonedChildren;
			this.forceUpdate();

			this.setState({ isLoading: true });

			const requestResponses = await Promise.all(saveRequests);

			this.setState({ isLoading: false });
			if (requestResponses.find((response) => response === undefined) !== undefined) {

				notification.error({
					message: "There was a problem while reordering, refresh and try again"
				});
			}
			else {
				notification.success({
					message: "Reorder Successful"
				});
			}
		}
	}

	private isDragDropCanceled = async () => {

		let isDragDropCanceled = true;

		// Check If In Draft
		if (this.state.isDraft) {

			// Get User
			const user = await LoginService.getUserIfExists();

			// Check User
			if (user !== undefined) {

				// Check If Template Allows Reordering
				if (this.section.template !== undefined && this.section.template.canReorderChildren) {

					// Get Whether Permitted
					const isPermitted = await PermissionService.userHasPermission(user, this.permission);

					// Check If Permitted
					if (isPermitted) {

						// Set Is NOT Canceled
						isDragDropCanceled = false;
					}
				}
			}
		}

		// Set Is Canceled
		this.setState({ isDragDropCanceled });
	}

	private onLoggedIn = async () => {

		// Reload Section
		await this.loadSection();
	}

	private onLoggedOut = async () => {

		// Reload Section
		await this.loadSection();
	}

	private loadSection = async (id?: number) => {

		// Indicate Section Is Loading
		StateService.setCancelableState(() => this.setState({ isLoading: true, isLoaded: false, validationErrors: undefined }), this.apiCancellationToken);

		// Get Section
		const section = await SectionApi.getSection(
			(id !== undefined ? id : (this.section !== undefined && this.section.id !== undefined ? this.section.id : this.props.match.params.id)),
			undefined
		);

		// Check Section
		if (section !== undefined) {

			// Get Published Version
			section.published = await SectionVersionApi.getPublished(section);

			// Check Published Version
			if (section.published !== undefined) {

				// Process Section
				const isProcessed = await this.processSection(section);

				// Check For Processed Section
				if (isProcessed) {

					// Indicate Section Is Loaded
					StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true }), this.apiCancellationToken);

					return;
				}
			}
			else {

				// Get Draft Version
				section.draft = await SectionVersionApi.getDraft(section);

				// Check Draft Version
				if (section.draft !== undefined) {

					// Process Section
					const isProcessed = await this.processSection(section);

					// Check For Processed Section
					if (isProcessed) {

						// Indicate Section Is Loaded
						StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true }), this.apiCancellationToken);

						return;
					}
				}
			}
		}

		// Indicate Section Is NOT Loaded
		StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: false }), this.apiCancellationToken);
	}

	private onEntryGroupDelete = async (_event: React.MouseEvent, child: OrderedEntryGroup) => {

		// Clone Current Version
		const clonedVersion = cloneDeep(this.currentVersion);

		// Locate Child In Draft
		const foundChildIndex = clonedVersion.entryGroups.findIndex((existingOrderedEntryGroup) => existingOrderedEntryGroup.item.id === child.item.id);

		// Check Found Child
		if (foundChildIndex !== -1) {

			// Indicate Entry Group Is Deleting
			StateService.setCancelableState(() => this.setState({ deletingEntryGroups: [...this.state.deletingEntryGroups, child.id!] }), this.apiCancellationToken);

			// Remove Child From Draft
			clonedVersion.entryGroups.splice(foundChildIndex, 1);

			// Delete Entry Group
			const isDeleted = await OrderedEntryGroupApi.delete(this.section, child);

			// Check Updated Draft
			if (isDeleted !== undefined) {

				// Process Updated Draft's Children
				await this.processSectionVersion(clonedVersion);
			}

			// Indicate Entry Group Is NOT Deleting
			StateService.setCancelableState(
				() => this.setState({ deletingEntryGroups: this.state.deletingEntryGroups.filter((deletingEntryGroup) => deletingEntryGroup !== child.id!) }),
				this.apiCancellationToken
			);
		}
	}

	private onListingGroupDelete = async (_event: React.MouseEvent, child: OrderedListingGroup) => {

		// Clone Current Version
		const clonedVersion = cloneDeep(this.currentVersion);

		// Locate Child In Draft
		const foundChildIndex = clonedVersion.listingGroups.findIndex((existingOrderedListingGroup) => existingOrderedListingGroup.item.id === child.item.id);

		// Check Found Child
		if (foundChildIndex !== -1) {

			// Indicate Listing Group Is Deleting
			StateService.setCancelableState(() => this.setState({ deletingListingGroups: [...this.state.deletingListingGroups, child.id!] }), this.apiCancellationToken);

			// Remove Child From Draft
			clonedVersion.listingGroups.splice(foundChildIndex, 1);

			// Delete Listing Group
			const isDeleted = await OrderedListingGroupApi.delete(this.section, child);

			// Check Updated Draft
			if (isDeleted !== undefined) {

				// Process Updated Draft's Children
				await this.processSectionVersion(clonedVersion);

				notification.success({
					message: "Successfully deleted child component"
				});
			}
			else {

				notification.error({
					message: "Failed to delete child component"
				});
			}

			// Indicate Listing Group Is NOT Deleting
			StateService.setCancelableState(
				() => this.setState({ deletingListingGroups: this.state.deletingListingGroups.filter((deletingListingGroup) => deletingListingGroup !== child.id!) }),
				this.apiCancellationToken
			);
		}
	}

	private onSectionClick = (orderedSection: OrderedSection) => {

		const section = orderedSection.item;

		// Check For Section
		if (section && section.id) {

			// Redirect To New Section
			this.props.history.push(`${SectionPage.BasePath}/${section.id}`);
		}
		else {

			// Redirect To Root Directory Page
			this.props.history.push(SectionPage.BasePath);
		}
	}

	private onSectionDelete = async (child: OrderedSection) => {

		// Clone Current Version
		const clonedVersion = cloneDeep(this.currentVersion);

		// Locate Child In Draft
		const foundChildIndex = clonedVersion.sections.findIndex((existingOrderedSection) => existingOrderedSection.item.id === child.item.id);

		// Check Found Child
		if (foundChildIndex !== -1) {

			// Remove Child From Draft
			clonedVersion.sections.splice(foundChildIndex, 1);

			// Process Updated Draft's Children
			await this.processSectionVersion(clonedVersion);
		}
	}

	private showDrawer = () => {

		StateService.setCancelableState(() => this.setState({ drawerVisible: true }), this.apiCancellationToken);
	}

	private onDrawerClose = () => {

		StateService.setCancelableState(() => this.setState({ drawerVisible: false }), this.apiCancellationToken);
	}

	private reloadDraft = async () => {

		// Indicate Is Reloading
		StateService.setCancelableState(() => this.setState({ isReloading: true }), this.apiCancellationToken);

		// Load Draft
		await this.loadDraft();

		// Indicate Is NOT Reloading
		StateService.setCancelableState(() => this.setState({ isReloading: false }), this.apiCancellationToken);
	}

	private loadDraft = async () => {

		// Get Draft Version
		const draft = await SectionApi.getDraft(this.section.id!);

		// Check Draft Version
		if (draft !== undefined) {

			// Process Draft Version
			await this.processSectionVersion(draft);
		}
		else {

			// Inform User An Error Occurred
			notification.error({
				message: "Unknown Error",
				description: "Unable to retrieve draft for editing.  Please contact system administrator."
			});
		}
	}

	private onDraftClose = async () => {

		// Indicate Is Reloading
		StateService.setCancelableState(() => this.setState({
			isReloading: true,
			isDraft: false,
			drawerVisible: false,
			deletingEntryGroups: [],
			deletingListingGroups: [],
			deletingSections: []
		}), this.apiCancellationToken);

		// Get Published Version
		const published = this.section.published;

		// Check For Published Version
		if (published !== undefined) {

			// Process Published Version
			await this.processSectionVersion(published);
		}

		// Indicate Draft Is NOT Displayed
		StateService.setCancelableState(() => this.setState({ isReloading: false }), this.apiCancellationToken);
	}

	private onDraftValidate = async () => {

		const isValidated = await SectionVersionApi.validateDraft(this.section);

		// Check Validation
		if (isValidated !== undefined) {

			if (isValidated instanceof ValidationError) {

				// Inform User
				notification.warning({
					message: "Validation Failed"
				});

				// Set Validation Errors
				StateService.setCancelableState(() => this.setState({ validationErrors: isValidated }), this.apiCancellationToken);
			}
			else {

				// Indicate Validation Succeeded
				StateService.setCancelableState(() => this.setState({ validationErrors: true }), this.apiCancellationToken);
			}
		}
		else {

			// Inform User
			notification.error({
				message: "Error Validating"
			});
		}
	}

	private onDraftNameChange = (name: string) => {

		// Set New Name On Version
		this.currentVersion.name = name;

		// Refresh Component
		this.forceUpdate();
	}

	private onDraftPublish = async (errors?: ValidationError) => {

		// Check For Errors
		if (errors !== undefined) {

			// Set Validation Errors
			StateService.setCancelableState(() => this.setState({ validationErrors: errors, drawerVisible: false }), this.apiCancellationToken);
		}
		else {

			// Get Latest Published Version (After Publishing)
			const publishedVersion = await SectionVersionApi.getPublished(this.section);

			// Check Latest Published
			if (publishedVersion !== undefined) {

				// Store Newly Published Version
				this.section.published = publishedVersion;

				// Process Newly Pulished Version
				await this.processSectionVersion(publishedVersion);

				// Disable Draft Mode
				StateService.setCancelableState(() => this.setState({ isDraft: false, drawerVisible: false, validationErrors: undefined }), this.apiCancellationToken);
			}
			else {

				// Inform User
				notification.error({
					message: "Error Retrieving Newly Published Record"
				});

				// Clear Validation Errors
				StateService.setCancelableState(() => this.setState({ validationErrors: undefined }), this.apiCancellationToken);
			}
		}
	}

	private onDraftRevert = async (newDraft: SectionVersion) => {

		// Store New Draft
		this.section.draft = newDraft;

		// Process New Draft
		await this.processSectionVersion(this.section.draft);

		// Ensure Still In Draft Mode
		StateService.setCancelableState(() => this.setState({ isDraft: true, drawerVisible: false }), this.apiCancellationToken);
	}

	private onExportClick = (event: ClickParam) => {

		// Redirect To Report Page
		this.props.history.push(`${ReportPage.BasePath}/${event.key}?sectionId=${this.section.id}`);
	}

	private onSearchHighlightToggle = () => {

		if (this.searchedWords.length > 0) {

			// Get Current Highlight Mode
			const willShowHighlighting = !this.state.showHighlighting;

			// Check Highlighting Mode
			if (willShowHighlighting) {

				// Reset Keywords
				this.highlightWords = this.searchedWords;
			}
			else {

				// Clear Keywords
				this.highlightWords = [];
			}

			// Toggle Keyword Highlighting
			StateService.setCancelableState(() => this.setState({ showHighlighting: willShowHighlighting }), this.apiCancellationToken);
		}
	}

	private onReturnToSearchButtonClick = () => {

		this.props.history.goBack();
	}

	private sortComponents = (a: OrderedSection | OrderedEntryGroup | OrderedListingGroup, b: OrderedSection | OrderedEntryGroup | OrderedListingGroup): number => {

		// Check For Identical Order
		if (a.order === b.order) {

			let aName: string = "";
			let bName: string = "";

			// Get 'A' Name
			switch (a.$type) {

				case OrderedEntryGroup.$type:

					aName = (a as OrderedEntryGroup).item.name; break;

				case OrderedListingGroup.$type:

					aName = (a as OrderedListingGroup).item.name; break;

				case OrderedSection.$type:

					const section = (a as OrderedSection).item;

					aName = (section.published !== undefined ? section.published.name : (section.draft !== undefined ? section.draft.name : "")); break;
			}

			// Get 'B' Name
			switch (b.$type) {

				case OrderedEntryGroup.$type:

					bName = (b as OrderedEntryGroup).item.name; break;

				case OrderedListingGroup.$type:

					bName = (b as OrderedListingGroup).item.name; break;

				case OrderedSection.$type:

					const section = (b as OrderedSection).item;

					bName = (section.published !== undefined ? section.published.name : (section.draft !== undefined ? section.draft.name : "")); break;
			}

			// Sort Alphabetically
			return aName.localeCompare(bName);
		}

		// Sort Numerically
		return a.order - b.order;
	}

	private processSearch = (currentLocation: Location) => {

		// Get Search Query
		const queryString = QueryString.parse(currentLocation.search).q;
		const keywords: string[] = [];
		let searchQuery: string | undefined;

		if (queryString) {

			// Check If Query Returns Multiple
			if (Array.isArray(queryString)) {

				// Concatenate Search Queries
				// keywords.push(...queryString);
				searchQuery = keywords.join(" ");
			}
			else {

				// Store Search Query As-Is
				// keywords.push(...(queryString as string).split(" "));
				searchQuery = queryString;
			}

			let strictKeywords: string[] = [];

			if (searchQuery !== undefined) {

				const { quotedValues, remainingString } = UtilityService.extractReduceQuotedStrings(searchQuery);
				searchQuery = remainingString;
				strictKeywords = quotedValues;
			}

			keywords.push(...searchQuery.split(" "));
			keywords.push(...strictKeywords);

			// Store Search Keywords
			this.searchedWords = keywords;
			this.highlightWords = keywords;

			// Indicate Search Results Should Show
			StateService.setCancelableState(() => this.setState({ showHighlighting: true }), this.apiCancellationToken);
		}
	}

	private processSection = async (section: Section): Promise<boolean> => {

		// Set Section
		this.section = section;

		// Check Section ID
		if (this.section.id !== undefined) {

			// Set Permission
			this.permission = new SingleRecordPermission(Right.Update, Section.$type, this.section.id);
		}
		else {

			// Set Permission
			this.permission = new Permission(Right.Update, Section.$type);
		}

		// Initialize Section Version
		let version: SectionVersion | undefined;

		// Get Section Hierarchy
		this.hierarchy = await SectionApi.getHierarchy(section.id);

		if (section.published !== undefined) {

			// Set Published Version
			version = section.published;
		}
		else if (section.draft !== undefined) {

			// Get Logged In User
			const loggedInUser = await LoginService.getUserIfExists();

			// Check User
			if (loggedInUser !== undefined) {

				// Get Whether User Can Edit Section
				const userCanEdit = await PermissionService.userHasPermission(loggedInUser, this.permission);

				// Check Permission
				if (userCanEdit !== undefined && userCanEdit) {

					// Set Draft Version
					version = section.draft;
				}
			}
		}

		// Check If Section Version Loaded
		if (version !== undefined) {

			// this.section.superceded = await SectionVersionApi.getSectionVersion(this.section.id!, VersionState.Superceded);

			// Check Version State
			if (version.state === VersionState.Draft) {

				// Load Draft
				await this.loadDraft();
			}
			else {

				// Process Section Version
				await this.processSectionVersion(version);
			}

			// Indicate Section Is Processed
			return true;
		}
		else {

			// Indicate Section Is NOT Processed
			return false;
		}
	}

	private processSectionVersion = async (version: SectionVersion) => {

		// Initialize Promises
		const promises: Array<Promise<void>> = [];

		// Get Latest Metadata
		promises.push(new Promise<void>(async (resolve) => {
			version.metadata = await MetadataApi.getAll(this.section, version, version.state === VersionState.Draft);
			resolve();
		}));

		// Get Latest Children
		promises.push(new Promise<void>(async (resolve) => {
			version.entryGroups = await OrderedEntryGroupApi.getAll(this.section, version, version.state === VersionState.Draft);
			resolve();
		}));
		promises.push(new Promise<void>(async (resolve) => {
			version.listingGroups = await OrderedListingGroupApi.getAll(this.section, version, version.state === VersionState.Draft);
			resolve();
		}));
		promises.push(new Promise<void>(async (resolve) => {
			version.sections = await OrderedSectionApi.getAll(this.section, version);
			resolve();
		}));

		// Await Requests
		await Promise.all(promises);

		// Initialize Should Try Reloading Flag
		let shouldTryReloading: boolean = false;

		// Initialize Draft Flag
		const isDraft: boolean = version.state === VersionState.Draft;

		// Check Version State
		switch (version.state) {

			case VersionState.Draft:

				// Get Section Template
				const template = await SectionApi.getTemplate(this.section, true);

				// Check Template
				if (template !== undefined) {

					// Set Template
					this.section.template = template;

					// Indicate A Reload Should/Could Be Attempted
					shouldTryReloading = true;
				}
				else {

					// Inform User
					notification.error({
						message: "Error Open Draft",
						description: "There was an error while opening the draft."
					});
				}

				break;

			case VersionState.Published:

				// Indicate A Reload Should/Could Be Attempted
				shouldTryReloading = true;

				break;
		}

		// Check If Should Try Reloading
		if (shouldTryReloading) {

			// Check For Changed Version
			if (!isEqual(version, this.currentVersion)) {

				// Organize Section Children
				this.children = [
					...version.entryGroups,
					...version.listingGroups,
					...version.sections
				].sort(this.sortComponents);

				// Set Version
				this.currentVersion = version;

				// Indicate Draft Is Displayed
				StateService.setCancelableState(() => this.setState({ isDraft }, this.isDragDropCanceled), this.apiCancellationToken);
			}
		}
	}

	private isSortingDisabled = (event: SortEvent | SortEventWithTag): boolean => {

		const elementsDisablingSorting = ["input", "textarea", "select", "option"];

		const isSortEventWithTag = (e: SortEvent | SortEventWithTag): e is SortEventWithTag => (e as SortEventWithTag).target.tagName !== undefined;

		if (this.state.isDragDropCanceled
			|| (isSortEventWithTag(event)
				&& elementsDisablingSorting.includes((event as SortEventWithTag).target.tagName.toLowerCase()))) {

			return true;
		}

		return false;
	}
}

interface ISortableComponent {
	section: Section;
	version: SectionVersion;
	component: OrderedEntryGroup | OrderedListingGroup | OrderedSection;
	permission: Permission;
	highlightingWords: string[];
	state: ISectionViewState;
	onEntryGroupDelete: (event: React.MouseEvent, entryGroup: OrderedEntryGroup) => void;
	onListingGroupDelete: (event: React.MouseEvent, listingGroup: OrderedListingGroup) => void;
	onSectionDelete: (section: OrderedSection) => void;
	onSectionClick: (section: OrderedSection) => void;
}

const SortableComponent = SortableElement<ISortableComponent>((props: ISortableComponent) => {

	// Check Component Type
	switch (props.component.$type) {

		case OrderedEntryGroup.$type:

			// Cast Component
			const orderedEntryGroup = props.component as OrderedEntryGroup;

			const entryGroupActions: Array<React.ReactElement<AuthorizedComponent<Button>>> = new Array<React.ReactElement<AuthorizedComponent<Button>>>();

			if (props.state.isDraft) {

				// Always add the group-delete button when showing draft
				entryGroupActions.push(
					(
						<AuthorizedComponent
							permission={props.permission}
							key="entry-group-delete"
						>
							<Tooltip title="Delete">
								<Button
									icon="delete"
									type="ghost"
									loading={props.state.deletingEntryGroups.includes(orderedEntryGroup.id!)}
									onClick={curry(props.onEntryGroupDelete)(__, orderedEntryGroup)}
								/>
							</Tooltip>
						</AuthorizedComponent>
					)
				);
			}

			// Return Rendered Entry Group
			return (

				<EntryGroupView
					className="section-entry-group-view"
					section={props.section}
					sectionVersion={props.version}
					orderedEntryGroup={orderedEntryGroup}
					highlightWords={props.highlightingWords}
					requiredPermission={props.permission}
					isEditable={props.state.isDraft}
					actions={entryGroupActions}
				/>
			);

		case OrderedListingGroup.$type:

			// Cast Component
			const orderedListingGroup = props.component as OrderedListingGroup;

			const listingGroupActions: Array<React.ReactElement<AuthorizedComponent<Button>>> = new Array<React.ReactElement<AuthorizedComponent<Button>>>();

			if (props.state.isDraft) {

				// Always add the group-delete button when showing draft
				listingGroupActions.push(
					(
						<AuthorizedComponent
							permission={props.permission}
							key="listing-group-delete"
						>
							<Tooltip title="Delete Section">
								<Button
									icon="delete"
									type="ghost"
									loading={props.state.deletingListingGroups.includes(orderedListingGroup.id!)}
									onClick={curry(props.onListingGroupDelete)(__, orderedListingGroup)}
								/>
							</Tooltip>
						</AuthorizedComponent>
					)
				);
			}

			// Return Rendered Listing Group
			return (

				<ListingGroupView
					className="section-view-listing-view"
					section={props.section}
					sectionVersion={props.version}
					orderedListingGroup={orderedListingGroup}
					highlightWords={props.highlightingWords}
					requiredPermission={props.permission}
					isEditable={props.state.isDraft}
					actions={listingGroupActions}
				/>
			);

		case OrderedSection.$type:

			// Cast Component
			const orderedSection = props.component as OrderedSection;

			if (orderedSection.item.published !== undefined || orderedSection.item.draft !== undefined) {

				return (

					<OrderedSectionView
						orderedSection={orderedSection}
						parentSection={props.section}
						sectionVersion={props.version}
						requiredPermission={props.permission}
						isDraft={props.state.isDraft}
						currentVersion={props.version}
						onClick={props.onSectionClick}
						highlightWords={props.highlightingWords}
						onSuccessfulDelete={props.onSectionDelete}
					/>
				);
			}
			else {

				return null;
			}

		default:

			// Return Nothing
			return null;
	}
});

interface ISortableComponentContainer {
	section: Section;
	version: SectionVersion;
	components: Array<OrderedEntryGroup | OrderedListingGroup | OrderedSection>;
	permission: Permission;
	highlightingWords: string[];
	state: ISectionViewState;
	onEntryGroupDelete: (event: React.MouseEvent, entryGroup: OrderedEntryGroup) => void;
	onListingGroupDelete: (event: React.MouseEvent, listingGroup: OrderedListingGroup) => void;
	onSectionDelete: (section: OrderedSection) => void;
	onSectionClick: (section: OrderedSection) => void;
}

const SortableComponentContainer = SortableContainer<ISortableComponentContainer>((props: ISortableComponentContainer) => {

	return (
		<section className="section-view-components">
			{props.components.map((value, index) => (
				<SortableComponent
					key={`${value.$type}-${value.id}`}
					index={index}
					section={props.section}
					version={props.version}
					component={value}
					permission={props.permission}
					highlightingWords={props.highlightingWords}
					state={props.state}
					onEntryGroupDelete={props.onEntryGroupDelete}
					onListingGroupDelete={props.onListingGroupDelete}
					onSectionDelete={props.onSectionDelete}
					onSectionClick={props.onSectionClick}
					{...props}
				/>
			))}
		</section>
	);
});
